import {
    IApiLeague, IApiLeagueContestant, IApiMatchResult, IApiRanking, IApiUserLeague,
    IApiLeagueRestriction, IApiContestant, IApiUserLeagueContestants, IApiContestantInfo,
    IApiFFARanking
} from "../typings/apiResponse";
import {LeagueQuery} from "./LeagueQuery";
import {IApiEndpoints} from "../typings/apiEndpoints";
import * as angular from "angular";

/**
 * @ngdoc service
 * @type {Object}
 * @name ngLeague.LeagueResource
 *
 * @requires $http
 * @requires ApiEndpoints
 *
 * @description
 * The LeagueResource exposes functions to query the ESL Play Api directly for league JSON responses.
 */
export class LeagueResource {
    public static $inject = ["$http", "ApiEndpoints"];
    constructor(
        private $http: angular.IHttpService,
        private ApiEndpoints: IApiEndpoints
    ) {}

    public getLeagues(leagueQuery: LeagueQuery): angular.IPromise<Array<IApiLeague>> {
        if (leagueQuery.id) {
            return this.getLeagueById(leagueQuery.id);
        }

        return this.getLeaguesByQuery(leagueQuery);
    }

    public getLeagueRestrictions(leagueId: number, contestantId?: number): angular.IPromise<IApiLeagueRestriction[]> {
        var url = this.ApiEndpoints.league + '/' + leagueId + '/signup';
        var filterString = this.appendParameterIfDefined('', 'contestantId', contestantId);

        if (filterString.length > 0) {
            url = url + filterString.replace('&', '?');
        }

        return this.$http.get(url).then(function(response: angular.IHttpPromiseCallbackArg<IApiLeagueRestriction[]>) {
            return response.data;
        });
    }

    public signUp(leagueId: number, contestantId: number): angular.IPromise<IApiContestant> {
        return this.$http.post(this.ApiEndpoints.league + '/' + leagueId + '/signup', {contestantId: contestantId}).then(
            function(contestantResponse: angular.IHttpPromiseCallbackArg<IApiContestant>) {
                return contestantResponse.data;
            }
        );
    }

    public checkIn(leagueId: number, contestantId: number): angular.IPromise<IApiContestant> {
        return this.$http.post(this.ApiEndpoints.league + '/' + leagueId + '/checkin', {contestantId: contestantId})
            .then((contestantResponse: angular.IHttpPromiseCallbackArg<IApiContestant>) => contestantResponse.data);
    }

    public signoff(leagueId: number, contestantId: number): angular.IPromise<angular.IHttpPromiseCallbackArg<any>> {
        return this.$http.post(`${this.ApiEndpoints.league}/${leagueId}/signoff`, {contestantId: contestantId});
    }

    public getRanking(query: LeagueQuery): angular.IPromise<IApiRanking> {
        var url = this.ApiEndpoints.league + '/' + query.id + '/ranking';
        var filterString: string = '';

        filterString = this.appendParameterIfDefined(filterString, 'contestantId', query.contestantId);

        if (!angular.isUndefined(query.limit)) {
            filterString = this.appendParameterIfDefined(filterString, 'limit', query.limit.total);
            filterString = this.appendParameterIfDefined(filterString, 'offset', query.limit.offset);
        }

        if (filterString.length > 0) {
            url = url + filterString.replace('&', '?');
        }

        return this.$http.get(url).then(
            function(rankingResponse: angular.IHttpPromiseCallbackArg<IApiRanking>) {
                return rankingResponse.data;
            }
        );
    }

    public getFFARanking(leagueId: number, round: number, group: number): angular.IPromise<IApiFFARanking> {
        return this.$http.get(`${this.ApiEndpoints.league}/${leagueId}/ranking?round=${round}&group=${group}`)
            .then((rankingResponse: angular.IHttpPromiseCallbackArg<IApiFFARanking>) => {
                return rankingResponse.data;
            });
    }

    public getMatchResults(leagueId: number): angular.IPromise<IApiMatchResult[]> {
        const url: string = this.ApiEndpoints.league + '/' + leagueId + '/results';

        return this.$http.get(url).then((res: angular.IHttpPromiseCallbackArg<IApiMatchResult[]>) => res.data);
    }

    public getLeagueContestants(leagueId: number): angular.IPromise<IApiLeagueContestant[]> {
        const url: string = this.ApiEndpoints.league + '/' + leagueId + '/contestants';

        return this.$http.get(url).then((res: angular.IHttpPromiseCallbackArg<IApiLeagueContestant[]>) => res.data);
    }

    public getUserLeagueContestants(userId: number) {
        const url = `${this.ApiEndpoints.user}/${userId}/leagues`;

        return this.$http.get(url).then((res: angular.IHttpPromiseCallbackArg<IApiUserLeagueContestants[]>) => res.data);
    }

    public getUserContestantInfos(leagueId: number, userId: number) {
        const url = `${this.ApiEndpoints.user}/${userId}/leagues/${leagueId}/contestants`;

        return this.$http.get(url).then((res: angular.IHttpPromiseCallbackArg<IApiContestantInfo[]>) => res.data);
    }

    private appendParameterIfDefined(oldParameters: string, parameterName: string, parameterValue: any): string {
        if (angular.isDefined(parameterValue)) {
            if (parameterValue instanceof Array) {
                return oldParameters.concat('&' + parameterName + '=' + parameterValue.join());
            } else {
                if (typeof parameterValue === 'string') {
                    return oldParameters.concat('&' + parameterName + '=' + parameterValue);
                }
                if (typeof parameterValue === 'number') {
                    return oldParameters.concat('&' + parameterName + '=' + parameterValue.toString());
                }
            }
        }

        return oldParameters;
    }

    private getLeagueById(leagueId: number): angular.IPromise<Array<IApiLeague>> {
        return this.$http.get(this.ApiEndpoints.league + '/' + leagueId).then(
            function(leagueResponse: angular.IHttpPromiseCallbackArg<IApiLeague>) {
                var leagueArray: Array<IApiLeague> = [];

                leagueArray.push(leagueResponse.data);

                return leagueArray;
            }
        );
    }

    private getLeaguesByQuery(leagueQuery: LeagueQuery): angular.IPromise<Array<IApiLeague>> {

        var url = this.ApiEndpoints.league;
        var filterString: string = '';

        filterString = this.appendParameterIfDefined(filterString, 'types', leagueQuery.types);
        filterString = this.appendParameterIfDefined(filterString, 'states', leagueQuery.states);
        filterString = this.appendParameterIfDefined(filterString, 'skillLevel', leagueQuery.skillLevels);
        filterString = this.appendParameterIfDefined(filterString, 'portals', leagueQuery.portals);
        filterString = this.appendParameterIfDefined(filterString, 'tags', leagueQuery.tags);
        filterString = this.appendParameterIfDefined(filterString, 'path', leagueQuery.path);
        filterString = this.appendParameterIfDefined(filterString, 'team_sizes', leagueQuery.teamSizes);
        filterString = this.appendParameterIfDefined(filterString, 'fields', leagueQuery.fields);
        filterString = this.appendParameterIfDefined(filterString, 'game_ids', leagueQuery.gameIds);
        filterString = this.appendParameterIfDefined(filterString, 'exclude_tags', leagueQuery.excludeTags);
        filterString = this.appendParameterIfDefined(filterString, 'league_ids', leagueQuery.leagueIds);
        filterString = this.appendParameterIfDefined(filterString, 'from', leagueQuery.from);
        filterString = this.appendParameterIfDefined(filterString, 'to', leagueQuery.to);
        filterString = this.appendParameterIfDefined(filterString, 'includeHidden', leagueQuery.includeHidden ? 1 : undefined);

        if (!angular.isUndefined(leagueQuery.limit)) {
            filterString = this.appendParameterIfDefined(filterString, 'limit.total', leagueQuery.limit.total);
            filterString = this.appendParameterIfDefined(filterString, 'limit.state.inProgress', leagueQuery.limit.inProgress);
            filterString = this.appendParameterIfDefined(filterString, 'limit.state.upcoming', leagueQuery.limit.upcoming);
            filterString = this.appendParameterIfDefined(filterString, 'limit.state.finished', leagueQuery.limit.finished);
        }

        if (filterString.length > 0) {
            url = url + filterString.replace('&', '?');
        }

        return this.$http.get(url).then(
            function(leaguesResponse: angular.IHttpPromiseCallbackArg<any>) {

                var leagueArray: Array<IApiLeague> = [];

                angular.forEach(leaguesResponse.data, function(league: IApiLeague) {
                    leagueArray.push(league);
                });

                return leagueArray;
            }
        );
    }
}
